export const CARE_MODELS = ['HOME_COMMUNITY', 'RESIDENTIAL', 'MIXED', 'UNKNOWN'] as const;
export type CareModel = (typeof CARE_MODELS)[number];

export const PROGRAM_FAMILIES = [
  'SAH',
  'HCP',
  'CHSP',
  'NDIS',
  'DVA',
  'TAC',
  'PRIVATE',
  'BROKERAGE',
  'PARTICIPANT_FUNDED',
  'STRC',
  'TRANSITION_CARE',
  'NATSIFACP',
  'MPSP',
  'FLEXIBLE_CARE',
  'RAC',
  'OTHER',
] as const;
export type ProgramFamily = (typeof PROGRAM_FAMILIES)[number];

export type RosterOperationalFamily = 'COMMUNITY' | 'RESIDENTIAL';

/**
 * Roster/workforce operations share one community care family across funding
 * programmes. This is not funding, navigation, billing or claiming authority.
 * All known non-RAC programmes (including OTHER) already belong to the
 * home/community roster vocabulary. Empty stays empty for the separate,
 * confirmed-empty workforce path; it must not imply community enrolment.
 */
export function rosterOperationalFamilies(
  families: readonly ProgramFamily[],
): readonly RosterOperationalFamily[] {
  return [
    ...new Set<RosterOperationalFamily>(
      families.map((family) => (family === 'RAC' ? 'RESIDENTIAL' : 'COMMUNITY')),
    ),
  ];
}

/** SIRS candidate regime: deliberately separate from programme identity. */
export const PROGRAM_CANDIDATE_REGIME = {
  SAH: 'AGED_CARE_ACT',
  SUPPORT_AT_HOME: 'AGED_CARE_ACT',
  SAH_STANDARD: 'AGED_CARE_ACT',
  SAH_ONGOING: 'AGED_CARE_ACT',
  SAH_INTERIM_MSO: 'AGED_CARE_ACT',
  SAH_TRANSITIONED_HCP: 'AGED_CARE_ACT',
  HCP_TRANSITION_NO_WORSE_OFF: 'AGED_CARE_ACT',
  HCP_TRANSITION_STANDARD_CONTRIBUTIONS: 'AGED_CARE_ACT',
  RESTORATIVE_CARE_PATHWAY: 'AGED_CARE_ACT',
  SAH_RESTORATIVE_PATHWAY: 'AGED_CARE_ACT',
  SAH_RESTORATIVE: 'AGED_CARE_ACT',
  END_OF_LIFE_PATHWAY: 'AGED_CARE_ACT',
  SAH_END_OF_LIFE_PATHWAY: 'AGED_CARE_ACT',
  SAH_END_OF_LIFE: 'AGED_CARE_ACT',
  HCP: 'AGED_CARE_ACT',
  HCP_ONGOING: 'AGED_CARE_ACT',
  HOME_CARE_PACKAGE: 'AGED_CARE_ACT',
  CHSP: 'AGED_CARE_ACT',
  CHSP_CURRENT: 'AGED_CARE_ACT',
  STRC: 'AGED_CARE_ACT',
  SHORT_TERM_RESTORATIVE_CARE: 'AGED_CARE_ACT',
  STRC_PRE_2025: 'AGED_CARE_ACT',
  TCP: 'AGED_CARE_ACT',
  TRANSITION_CARE: 'AGED_CARE_ACT',
  NATSIFACP: 'AGED_CARE_ACT',
  MPSP: 'AGED_CARE_ACT',
  FLEXIBLE_CARE: 'AGED_CARE_ACT',
  RAC: 'AGED_CARE_ACT',
  RAC_PERMANENT: 'AGED_CARE_ACT',
  RAC_PRE_2025_ARRANGEMENT: 'AGED_CARE_ACT',
  RAC_RESPITE: 'AGED_CARE_ACT',
  RAC_AN_ACC: 'AGED_CARE_ACT',
  RESIDENTIAL: 'AGED_CARE_ACT',
  RESIDENTIAL_CARE: 'AGED_CARE_ACT',
  AN_ACC: 'AGED_CARE_ACT',
  RESIDENTIAL_AN_ACC: 'AGED_CARE_ACT',
  NDIS: 'NDIS',
  DVA: 'DVA',
  TAC: 'TAC',
  PRIVATE: 'PRIVATE',
  BROKERAGE: 'UNKNOWN',
  PARTICIPANT: 'UNKNOWN',
  PARTICIPANT_FUNDED: 'UNKNOWN',
  OTHER: 'UNKNOWN',
  NON_SAH_FUNDED: 'UNKNOWN',
  GENERIC: 'UNKNOWN',
  UNKNOWN: 'UNKNOWN',
  SAH_AT_HM: 'UNKNOWN',
  MIXED: 'UNKNOWN',
} as const;
export type ProgramCandidateRegime =
  (typeof PROGRAM_CANDIDATE_REGIME)[keyof typeof PROGRAM_CANDIDATE_REGIME];

export const FIREWALL_FAMILIES = ['SAH', 'RAC', 'SHARED', 'OTHER', 'UNKNOWN'] as const;
export type FirewallFamily = (typeof FIREWALL_FAMILIES)[number];

export const RESOLUTION_STATUSES = [
  'RESOLVED_SINGLE',
  'RESOLVED_DUAL',
  'RESOLVED_MULTI',
  'HISTORICAL_ONLY',
  'CONFLICT',
  'UNKNOWN',
] as const;
export type ResolutionStatus = (typeof RESOLUTION_STATUSES)[number];

export const PROGRAM_VARIANTS = [
  'SAH_ONGOING',
  'SAH_INTERIM_MSO',
  'SAH_TRANSITIONED_HCP',
  'SAH_RESTORATIVE',
  'SAH_END_OF_LIFE',
  'HCP_ONGOING',
  'CHSP_CURRENT',
  'STRC_PRE_2025',
  'RAC_PERMANENT',
  'RAC_RESPITE',
  'RAC_AN_ACC',
  'LEGACY',
  'OTHER',
] as const;
export type ProgramVariant = (typeof PROGRAM_VARIANTS)[number];

export type ProgramLifecycle = 'CURRENT' | 'HISTORICAL' | 'FUTURE';
export type ProgramSourceAuthority = 'AUTHORITATIVE' | 'CORROBORATING' | 'LEGACY_DERIVED';
export type ProgramSourceKind =
  | 'PROGRAM_ENROLLMENT'
  | 'CLIENT_FUNDING'
  | 'RESIDENTIAL_ADMISSION'
  | 'SERVICE'
  | 'SHIFT'
  | 'WORKFLOW'
  | 'TASK'
  | 'STATEMENT'
  | 'NOTIFICATION'
  | 'DOCUMENT';

export interface ProgramBindingProvenance {
  sourceKind: ProgramSourceKind;
  sourceId: string;
  authority: ProgramSourceAuthority;
  lifecycle: ProgramLifecycle;
  effectiveFrom?: string;
  effectiveTo?: string;
  sourceRevision?: string;
  observedAt: string;
}

export interface ProgramBinding {
  bindingId: string;
  family: ProgramFamily;
  variant?: ProgramVariant;
  overlays: readonly 'SAH_AT_HM'[];
  facets: readonly ('RAC_PRE_2025_ARRANGEMENT' | 'CHSP_PRE_2027')[];
  fundingCohort?: string;
  firewallFamily: Exclude<FirewallFamily, 'SHARED' | 'UNKNOWN'>;
  lifecycle: ProgramLifecycle;
  provenance: readonly ProgramBindingProvenance[];
}

export interface ExperienceContext {
  tenantId: string;
  actorUserId: string;
  role: string;
  tenantCareModel: CareModel;
  eligibleProgramFamilies: readonly ProgramFamily[];
  programBindings: readonly ProgramBinding[];
  resolutionStatus: ResolutionStatus;
  activeBindingId?: string;
  activeProgramFamily: ProgramFamily | 'SHARED' | 'UNKNOWN';
  firewallFamily: FirewallFamily;
  selectedPersonId?: string;
  selectedFacilityId?: string;
  selectedServiceAreaId?: string;
  selectedServiceId?: string;
  selectedShiftId?: string;
  selectedWorkflowId?: string;
  selectedRecord?: { type: string; id: string };
  allowedFacilityIds: readonly string[];
  allowedServiceAreaIds: readonly string[];
  enabledModules: readonly string[];
  representativeAuthority?: readonly string[];
  productionSurfaceContext: 'SUPPORTED' | 'PILOT' | 'BLOCKED' | 'INTERNAL';
  contextRevision: string;
  projectionSchemaVersion: string;
  resolverVersion: string;
  authorityRevision: string;
  resolvedAt: string;
  staleAfter: string;
}

export interface ProgramTokenMapping {
  sourceToken: string;
  family: ProgramFamily | null;
  firewallFamily: Exclude<FirewallFamily, 'SHARED'>;
  variant?: ProgramVariant;
  overlay?: 'SAH_AT_HM';
  historicalFamily?: ProgramFamily;
  rule: string;
}

const mapping = (
  sourceToken: string,
  family: ProgramFamily | null,
  firewallFamily: Exclude<FirewallFamily, 'SHARED'>,
  rule: string,
  options: Pick<ProgramTokenMapping, 'variant' | 'overlay' | 'historicalFamily'> = {},
): ProgramTokenMapping => ({ sourceToken, family, firewallFamily, rule, ...options });

export const PROGRAM_SOURCE_TOKEN_MAPPINGS = [
  mapping(
    'SAH',
    'SAH',
    'SAH',
    'Current only when an authoritative source and effective period are valid.',
    {
      variant: 'SAH_ONGOING',
    },
  ),
  mapping('SAH_ONGOING', 'SAH', 'SAH', 'Canonical current SAH program enrollment.', {
    variant: 'SAH_ONGOING',
  }),
  mapping(
    'SUPPORT_AT_HOME',
    'SAH',
    'SAH',
    'Current only when an authoritative source and effective period are valid.',
    { variant: 'SAH_ONGOING' },
  ),
  mapping('SAH_STANDARD', 'SAH', 'SAH', 'Funding cohort for a current SAH binding.', {
    variant: 'SAH_ONGOING',
  }),
  mapping('SAH_INTERIM_MSO', 'SAH', 'SAH', 'Retain the exact interim cohort and offer state.', {
    variant: 'SAH_INTERIM_MSO',
  }),
  mapping(
    'SAH_TRANSITIONED_HCP',
    'SAH',
    'SAH',
    'Canonical current transitioned-HCP SAH program enrollment.',
    { variant: 'SAH_TRANSITIONED_HCP', historicalFamily: 'HCP' },
  ),
  mapping(
    'HCP_TRANSITION_NO_WORSE_OFF',
    'SAH',
    'SAH',
    'Current transitioned SAH binding with separately retained historical HCP provenance.',
    { variant: 'SAH_TRANSITIONED_HCP', historicalFamily: 'HCP' },
  ),
  mapping(
    'HCP_TRANSITION_STANDARD_CONTRIBUTIONS',
    'SAH',
    'SAH',
    'Current transitioned SAH binding with separately retained historical HCP provenance.',
    { variant: 'SAH_TRANSITIONED_HCP', historicalFamily: 'HCP' },
  ),
  mapping('RESTORATIVE_CARE_PATHWAY', 'SAH', 'SAH', 'Short-term restorative SAH pathway.', {
    variant: 'SAH_RESTORATIVE',
  }),
  mapping('SAH_RESTORATIVE_PATHWAY', 'SAH', 'SAH', 'Short-term restorative SAH pathway.', {
    variant: 'SAH_RESTORATIVE',
  }),
  mapping('SAH_RESTORATIVE', 'SAH', 'SAH', 'Canonical current restorative SAH enrollment.', {
    variant: 'SAH_RESTORATIVE',
  }),
  mapping('END_OF_LIFE_PATHWAY', 'SAH', 'SAH', 'SAH end-of-life pathway.', {
    variant: 'SAH_END_OF_LIFE',
  }),
  mapping('SAH_END_OF_LIFE_PATHWAY', 'SAH', 'SAH', 'SAH end-of-life pathway.', {
    variant: 'SAH_END_OF_LIFE',
  }),
  mapping('SAH_END_OF_LIFE', 'SAH', 'SAH', 'Canonical current SAH end-of-life enrollment.', {
    variant: 'SAH_END_OF_LIFE',
  }),
  mapping('HCP', 'HCP', 'OTHER', 'Never infer SAH without an authoritative transition cohort.', {
    variant: 'HCP_ONGOING',
  }),
  mapping(
    'HCP_ONGOING',
    'HCP',
    'OTHER',
    'Canonical current HCP program enrollment; never infer SAH.',
    { variant: 'HCP_ONGOING' },
  ),
  mapping('HOME_CARE_PACKAGE', 'HCP', 'OTHER', 'Legacy HCP synonym; never infer SAH.', {
    variant: 'HCP_ONGOING',
  }),
  mapping('CHSP', 'CHSP', 'OTHER', 'Distinct community program identity.', {
    variant: 'CHSP_CURRENT',
  }),
  mapping('CHSP_CURRENT', 'CHSP', 'OTHER', 'Canonical current CHSP program enrollment.', {
    variant: 'CHSP_CURRENT',
  }),
  mapping('NDIS', 'NDIS', 'OTHER', 'Never inherit SAH terminology from home delivery.'),
  mapping('DVA', 'DVA', 'OTHER', 'Distinct funding and content profile.'),
  mapping('TAC', 'TAC', 'OTHER', 'Distinct funding and content profile.'),
  mapping('PRIVATE', 'PRIVATE', 'OTHER', 'Private-pay semantics, not SAH contribution semantics.'),
  mapping('BROKERAGE', 'BROKERAGE', 'OTHER', 'Explicit brokerage profile.'),
  mapping('PARTICIPANT', 'PARTICIPANT_FUNDED', 'OTHER', 'Explicit participant-funded profile.'),
  mapping(
    'PARTICIPANT_FUNDED',
    'PARTICIPANT_FUNDED',
    'OTHER',
    'Explicit participant-funded profile.',
  ),
  mapping('STRC', 'STRC', 'OTHER', 'Legacy short-term restorative program, not SAH.', {
    variant: 'STRC_PRE_2025',
  }),
  mapping(
    'SHORT_TERM_RESTORATIVE_CARE',
    'STRC',
    'OTHER',
    'Legacy short-term restorative program.',
    {
      variant: 'STRC_PRE_2025',
    },
  ),
  mapping(
    'STRC_PRE_2025',
    'STRC',
    'OTHER',
    'Legacy-only unless the authoritative source permits otherwise.',
    {
      variant: 'STRC_PRE_2025',
    },
  ),
  mapping('TCP', 'TRANSITION_CARE', 'OTHER', 'Distinct transition-care identity.'),
  mapping('TRANSITION_CARE', 'TRANSITION_CARE', 'OTHER', 'Distinct transition-care identity.'),
  mapping('NATSIFACP', 'NATSIFACP', 'OTHER', 'Distinct program identity.'),
  mapping('MPSP', 'MPSP', 'OTHER', 'Distinct program identity.'),
  mapping('FLEXIBLE_CARE', 'FLEXIBLE_CARE', 'OTHER', 'Distinct program identity.'),
  mapping('RAC', 'RAC', 'RAC', 'Requires a current authoritative residential source.', {
    variant: 'RAC_PERMANENT',
  }),
  mapping(
    'RAC_PERMANENT',
    'RAC',
    'RAC',
    'Canonical current permanent residential program enrollment.',
    { variant: 'RAC_PERMANENT' },
  ),
  mapping(
    'RAC_PRE_2025_ARRANGEMENT',
    'RAC',
    'RAC',
    'Historical fee-arrangement facet on a current RAC enrollment.',
    { variant: 'RAC_PERMANENT' },
  ),
  mapping('RESIDENTIAL', 'RAC', 'RAC', 'Requires a current authoritative residential source.', {
    variant: 'RAC_PERMANENT',
  }),
  mapping('AN_ACC', 'RAC', 'RAC', 'Residential funding/classification variant.', {
    variant: 'RAC_AN_ACC',
  }),
  mapping('RAC_RESPITE', 'RAC', 'RAC', 'Residential respite binding.', {
    variant: 'RAC_RESPITE',
  }),
  mapping(
    'RESIDENTIAL_AN_ACC',
    'RAC',
    'RAC',
    'Requires an authoritative AN-ACC/residential source.',
    {
      variant: 'RAC_AN_ACC',
    },
  ),
  mapping(
    'NON_SAH_FUNDED',
    null,
    'UNKNOWN',
    'Negative cohort only; never infer a positive program.',
  ),
  mapping('SAH_AT_HM', null, 'UNKNOWN', 'Overlay only; cannot create a SAH binding.', {
    overlay: 'SAH_AT_HM',
  }),
  mapping('OTHER', 'OTHER', 'OTHER', 'Requires an explicit registered content profile.'),
  mapping('GENERIC', null, 'UNKNOWN', 'Fail closed; never coerce to OTHER or SAH.'),
  mapping('UNKNOWN', null, 'UNKNOWN', 'Fail closed; never coerce to OTHER or SAH.'),
] as const satisfies readonly ProgramTokenMapping[];

const TOKEN_MAP = new Map(PROGRAM_SOURCE_TOKEN_MAPPINGS.map((row) => [row.sourceToken, row]));

export function normalizeProgramSourceToken(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  return normalized.length > 0 ? normalized : null;
}

export function mapProgramSourceToken(value: unknown): ProgramTokenMapping {
  const normalized = normalizeProgramSourceToken(value);
  if (!normalized) {
    return mapping('', null, 'UNKNOWN', 'Missing source token fails closed.');
  }
  return (
    TOKEN_MAP.get(normalized) ??
    mapping(
      normalized,
      null,
      'UNKNOWN',
      'Unmapped source token fails closed pending an explicit mapping row.',
    )
  );
}

export interface PresentationResolution {
  status: ResolutionStatus;
  activeProgramFamily: ProgramFamily | 'SHARED' | 'UNKNOWN';
  firewallFamily: FirewallFamily;
  activeBindingId?: string;
  reason: string;
}

export function resolvePresentationBinding(
  bindings: readonly ProgramBinding[],
  activeBindingId?: string,
): PresentationResolution {
  const current = bindings.filter((binding) => binding.lifecycle === 'CURRENT');
  const authoritative = current.filter((binding) =>
    binding.provenance.some((source) => source.authority === 'AUTHORITATIVE'),
  );

  if (authoritative.length === 0) {
    return bindings.some((binding) => binding.lifecycle === 'HISTORICAL')
      ? {
          status: 'HISTORICAL_ONLY',
          activeProgramFamily: 'UNKNOWN',
          firewallFamily: 'UNKNOWN',
          reason: 'No authoritative current binding exists.',
        }
      : {
          status: 'UNKNOWN',
          activeProgramFamily: 'UNKNOWN',
          firewallFamily: 'UNKNOWN',
          reason: 'No authoritative program evidence exists.',
        };
  }

  const bindingIds = new Set(authoritative.map((binding) => binding.bindingId));
  if (bindingIds.size !== authoritative.length) {
    return {
      status: 'CONFLICT',
      activeProgramFamily: 'UNKNOWN',
      firewallFamily: 'UNKNOWN',
      reason: 'Duplicate authoritative binding identifiers are contradictory.',
    };
  }

  if (activeBindingId) {
    const selected = authoritative.find((binding) => binding.bindingId === activeBindingId);
    if (!selected) {
      return {
        status: 'CONFLICT',
        activeProgramFamily: 'UNKNOWN',
        firewallFamily: 'UNKNOWN',
        reason: 'The selected binding is not a current authoritative binding.',
      };
    }
    return {
      status:
        authoritative.length === 1
          ? 'RESOLVED_SINGLE'
          : authoritative.length === 2
            ? 'RESOLVED_DUAL'
            : 'RESOLVED_MULTI',
      activeProgramFamily: selected.family,
      firewallFamily: selected.firewallFamily,
      activeBindingId: selected.bindingId,
      reason: 'An explicit current authoritative binding is selected.',
    };
  }

  if (authoritative.length === 1) {
    const selected = authoritative[0];
    if (!selected) throw new Error('unreachable_missing_single_binding');
    return {
      status: 'RESOLVED_SINGLE',
      activeProgramFamily: selected.family,
      firewallFamily: selected.firewallFamily,
      activeBindingId: selected.bindingId,
      reason: 'Exactly one current authoritative binding exists.',
    };
  }

  return {
    status: authoritative.length === 2 ? 'RESOLVED_DUAL' : 'RESOLVED_MULTI',
    activeProgramFamily: 'SHARED',
    firewallFamily: 'SHARED',
    reason: 'Multiple current authoritative bindings require an explicit selection.',
  };
}
